import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { IconButton, Menu, MenuItem } from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"

import placeHolder from "../../../../assets/images/placeholder.png"
import { UText } from "../../../../components"
import { Box, Grid } from "../../../../components/mui.components"
import PatientBasicInfo from "../../../../containers/LandingLayout/PatientInfoHeader/PatientBasicInfo"
import { useAppDispatch, useAppSelector } from "../../../../core/app/hooks"
import { RootState } from "../../../../core/app/store"
import { ICase } from "../../../../core/model/interface/ICase"
import { IPatient } from "../../../../core/model/interface/IPatient"
import { UI_PERMISSION } from "../../../../core/utils/access/permissionConstants"
import { useMyNavigation } from "../../../../hooks/useMyNavigation"
import usePermissions from "../../../../hooks/userPermission"
import { getStatusObjByKey } from "../../config/status.config"
import { calculateAge, formatDate } from "../../PatientList/util/commonUtil"
import EditPatientNotePopup from "../EditPatientNotePopup"
import { addNewPatient, fetchPatientById } from "@/core/app/slices/patients"
import { setData } from "@/core/app/slices/patients/patientSlice"
import { openNewTab } from "../../config/tab.config"
import { useTabRef } from "@/routes/TabProvider"
import { TabExceededPopup } from "../../PatientList/components/UTable/TabExceededPopup"
import { getCaseByPatient } from "@/core/app/slices/case"

const PatientInfoHeader: FC<{
  status: string
  patientData: IPatient
  caseDetail: ICase
  setIsAlertOpen: (type: "addNewCase" | "OPEN_CASE_ARCHIVE_MODEL" | "OPEN_CANCEL_MODEL" | "OPEN_ARCHIVE_PATIENT_MODEL" | "OPEN_CASE_UNARCHIVE_MODEL" | "OPEN_ASSIGN_MODEL" | "") => void
}> = ({ status, patientData, caseDetail, setIsAlertOpen }) => {
  const dispatch = useAppDispatch()
  const { hasAny } = usePermissions()
  const { t } = useTranslation()
  const navigate = useMyNavigation()
  const { isExistingPatientCheckModal } = useAppSelector(
    (state: RootState) => state.patientService,)  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { patientId } = useParams()
  const tabRefs = useTabRef()
  const { orgId } = useParams()
  const { user } = useAppSelector((state: RootState) => state.userService)
  const [openTabExceededModal, setOpenTabExceededModal] = useState(false)

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const { profile } = useAppSelector((state: RootState) => state.xrayService)

  const [age, setAge] = useState("")
  const [dateOfBirth, setDateOfBirth] = useState("")

  const PATIENT_BASIC_INFO = [
    { label: t("overview.patientInfo.age"), value: age },
    { label: t("overview.patientInfo.born"), value: dateOfBirth },
  ]

  const [openNote, setOpenNote] = useState(false)
  const handlePopup = () => setOpenNote((x) => !x)

  const { caseList } = useAppSelector((state: RootState) => state.caseService)

  // TODO : have to change the hardcoded values during api integration
  const PATIENT_ADDITIONAL_INFO = [
    { label: t("overview.patientInfo.phone"), value: "(415) 938-7714" },
    {
      label: t("overview.patientInfo.email"),
      value: "leanne.hamilton@mercedesamg.com",
    },
  ]

  const actionList = getStatusObjByKey(status)

  useEffect(() => {
    if (!patientData?.dob) return
    setAge(calculateAge(patientData?.dob))
    setDateOfBirth(formatDate(patientData?.dob))
  }, [patientData?.dob])

  const checkforActiveCase = () => {
    const payload = {
      firstname: patientData.firstName,
      lastname: patientData.lastName,
      dob: patientData.dob,
      patient_id: patientData.id,
      assigned_to: caseList[0]?.assigned_to || null, // set assignee same as recent case's assignee, for new case
    }
    dispatch(addNewPatient(payload)).then((res) => {
      dispatch(setData({ newPatientAPI: "idle" }))
      if (res.payload?.data?.status === "Success") {
        const case_id = res.payload.data.result?.case?.id
        openNewTab({
          route: `/patients/choosetype/patient/${patientId}/case/${case_id}`,
          tabRefs,
          setOpenTabExceededModal,
          orgId,
          isAllowToViewAllOrg: user?.isAllowToViewAllOrg,
        })
        
        dispatch(getCaseByPatient({ patientId: patientId, isCaseArchived: true }))
        dispatch(fetchPatientById({ patientId }))
      } else {
        setIsAlertOpen("addNewCase")
      }
    })
  }

  return (
    <Grid
      container
      direction={"row"}
      spacing={0}
      justifyContent={"center"}
      sx={{ mb: 2 }}
    >
      <Grid item sm={6} sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component={"img"}
          src={profile[0]?.src || placeHolder}
          alt={"Logo"}
          loading="lazy"
          sx={{ mr: 2, height: "96px", width: "96px" }}
        />
        <PatientBasicInfo patientData={patientData} caseDetail={caseDetail} />
      </Grid>
      <Grid
        item
        sm={6}
        sx={{ display: "flex", justifyContent: "space-between", pl: 3 }}
      >
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "136px",
            mr: 2,
            pt: 2,
          }}
        >
          {PATIENT_BASIC_INFO.map((info) => {
            return (
              <Box
                component={"div"}
                sx={{ display: "flex", flexDirection: "row" }}
                key={info.label}
              >
                <UText
                  variant={"caption"}
                  color="rgba(0, 0, 0, 0.6)"
                  sxProp={{ width: "48px", fontWeight: "700" }}
                >
                  {info.label}
                </UText>
                <UText variant={"caption"} color="rgba(0, 0, 0, 0.6)">
                  {info.value}
                </UText>
              </Box>
            )
          })}
          <Box component={"div"} sx={{ display: "flex", flexDirection: "row" }}>
            <UText
              variant={"caption"}
              color="rgba(0, 0, 0, 0.6)"
              sxProp={{ width: "48px", fontWeight: "700" }}
            >
              {t("formfields.notes")}
            </UText>
            <UText
              variant={"caption"}
              color="rgba(0, 0, 0, 0.6)"
              onClickHandler={handlePopup}
              sxProp={{ textDecoration: "underline", cursor: "pointer" }}
            >
              {t("formfields.view")}
            </UText>
          </Box>
          <EditPatientNotePopup
            handlePopup={handlePopup}
            openNote={openNote}
            note={patientData.notes}
          />
        </Grid>
        {actionList && actionList?.showAdditionalData && (
          <Grid item sx={{ mr: 2, mt: 2 }}>
            {PATIENT_ADDITIONAL_INFO.map((info) => {
              return (
                <Box
                  key={info.label}
                  component={"div"}
                  sx={{ display: "flex", flexDirection: "row" }}
                >
                  <UText
                    variant={"caption"}
                    color="rgba(0, 0, 0, 0.6)"
                    sxProp={{ width: "64px", fontWeight: "700" }}
                  >
                    {info.label}
                  </UText>
                  <UText
                    variant={"caption"}
                    color="rgba(0, 0, 0, 0.6)"
                    sxProp={{ maxWidth: "255px" }}
                  >
                    {info.value}
                  </UText>
                </Box>
              )
            })}
          </Grid>
        )}
        {hasAny(UI_PERMISSION.UI_PATIENTS_UPDATE) && !isExistingPatientCheckModal && (
          <Grid item>
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{ mt: 1 }}
              aria-controls={open ? 'menu-list' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-list"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'menu-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem 
                key="editPatient" 
                onClick={() => {
                  navigate(`/overview/patient/${patientData.id}/settings`)
                  handleMenuClose()
                }}
                sx={{ justifyContent: 'flex-end' }}
              >
                {t("overview.buttons.editPatientBtn")}
              </MenuItem>
              <MenuItem 
                key="addNewCase" 
                onClick={() => {
                  checkforActiveCase()
                  handleMenuClose()
                }}
                sx={{ justifyContent: 'flex-end' }}
              >
                {t("overview.buttons.addNewCaseBtn")}
              </MenuItem>
            </Menu>
          </Grid>
        )}
      </Grid>
      <TabExceededPopup
        openTabExceededModal={openTabExceededModal}
        setOpenTabExceededModal={setOpenTabExceededModal}
      />
    </Grid>
  )
}

export default PatientInfoHeader
